import { Search } from "lucide-react";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface SearchableRecord {
  symbol: string;
}

export const useStockSearch = <T extends SearchableRecord>(
  records: T[],
  selectedRow: { symbol: string } | undefined | null,
  onRowSelect: ((row: T) => void) | undefined,
  dropdownId: string,
  idSuffix: string = "",
) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const showDropdownRef = useRef(false);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const allSymbols = useMemo(() => records.map((d) => d.symbol), [records]);

  const filteredSymbols = useMemo(() => {
    if (!debouncedSearch.trim()) return Array.from(new Set(allSymbols));
    const lower = debouncedSearch.toLowerCase();
    // Filter out duplicates and match search
    return Array.from(new Set(allSymbols)).filter((sym) =>
      sym.toLowerCase().includes(lower),
    );
  }, [debouncedSearch, allSymbols]);

  useEffect(() => {
    if (showDropdownRef.current) {
      if (selectedRow?.symbol) {
        const rowSymbol = selectedRow.symbol.replace(/\.NS$/, "");
        const el = document.getElementById(
          `row-${rowSymbol}${idSuffix ? `-${idSuffix}` : ""}`,
        );
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      showDropdownRef.current = false;
    }
  }, [selectedRow, idSuffix, scrollTrigger]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdownEl = document.getElementById(dropdownId);
      if (dropdownEl && !dropdownEl.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownId]);

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    setShowDropdown(true);
  };

  const handleSuggestionClick = (found: T) => {
    showDropdownRef.current = true;
    onRowSelect?.(found);
    setSearchInput("");
    setShowDropdown(false);
    setScrollTrigger((prev) => prev + 1);
  };

  return {
    searchInput,
    setSearchInput,
    filteredSymbols,
    showDropdown,
    setShowDropdown,
    onSearchChange,
    handleSuggestionClick,
  };
};

interface StockSearchInputProps<T> {
  searchInput: string;
  onSearchChange: (value: string) => void;
  setShowDropdown: (value: boolean) => void;
  showDropdown: boolean;
  filteredSymbols: string[];
  records: T[];
  handleSuggestionClick: (item: T) => void;
  dropdownId: string;
  setFreeTrialRestrictModal?: Dispatch<SetStateAction<boolean>>;
  freeUser?: boolean;
}

export const StockSearchInput = <T extends SearchableRecord>({
  searchInput,
  onSearchChange,
  setShowDropdown,
  showDropdown,
  filteredSymbols,
  records,
  handleSuggestionClick,
  dropdownId,
  setFreeTrialRestrictModal,
  freeUser,
}: StockSearchInputProps<T>) => {
  return (
    <div className="relative w-48 mr-2" id={dropdownId}>
      <input
        type="text"
        placeholder="Search stock symbol..."
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        onClick={() => freeUser && setFreeTrialRestrictModal?.(true)}
        onFocus={() => setShowDropdown(true)}
        className="w-full pl-8 pr-2 p-1 text-sm bg-white dark:bg-[#1E1E2D] border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-transparent text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500"
      />
      <Search className="cursor-pointer absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-gray-400" />

      {!freeUser && showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#1E1E2D] border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
          {filteredSymbols.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-xs">
              No Record Found
            </div>
          ) : (
            filteredSymbols.map((sym) => (
              <div
                key={sym}
                onClick={() => {
                  const found = records.find((d) => d.symbol === sym);
                  if (found) {
                    handleSuggestionClick(found);
                  }
                }}
                className="px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
              >
                {sym}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
